/** @jsx jsx */
// eslint-disable-next-line no-unused-vars
import React, { useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from '@cvent/carina-theme-provider';
import { jsx } from '@emotion/core';
import { getStyle } from './iconGetStyle';
import { BreakpointsContext } from '@cvent/carina-layout';
const API = {
  /** the size of the icon. This follows the same guidelines as the icons found in the carina-icons package.  */

  size: PropTypes.oneOf(['s', 'm', 'l', 'xl']),
  /** the id*/
  id: PropTypes.string
};
// These icons are here and not there because of the complexity.
// These are both animated as well as all of them having multiple colours.

export const LoadingIcon = ({ size = 'm', id }) => {
  const style = {
    ...useStyle(size)
  };
  let keyCount = 1;
  function getKey() {
    if (keyCount < Number.MAX_SAFE_INTEGER - 1) {
      return keyCount++;
    }
    return (keyCount = 1);
  }
  // it has to be like this to pass the tests, since the buttons and textboxes don't pass an id prop to the icon, meaning if I randomly generated an id, it would be different each test, meaning that they would always fail
  const strokeId = useRef(id || getKey());
  // each circle needs its own unique id so it does not interfere with other ones, if they are on the same page
  // making it like this probably isn't the best way of doing it, but it is something that works and lets me get back to other things in the mean time
  return (
    <div css={style.outerContainerStyle}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        css={style.circleStyle}
        fill="transparent"
      >
        <defs>
          <linearGradient id={strokeId.current}>
            <stop offset="0%" stopColor={style.gradientColor1} />
            <stop offset="50%" stopColor={style.gradientColor2} />
            <stop offset="100%" stopColor={style.gradientColor3} />
          </linearGradient>
        </defs>
        <circle
          cx="50%"
          cy="50%"
          r={'45%'}
          stroke={style.rail}
          strokeLinecap="round"
          fill="transparent"
        />
        <circle
          stroke={`url(#${strokeId.current})`}
          cx="50%"
          cy="50%"
          r={'45%'}
          css={{
            ...style.tailChaserStyle
          }}
        />
      </svg>
    </div>
  );
};

LoadingIcon.propTypes = API;

function useStyle(size) {
  const theme = useContext(ThemeContext);
  const { prefersReducedMotion } = useContext(BreakpointsContext);
  const {
    outerContainer,
    circle,
    tailChaser,
    // these are features to be released later
    // dangerColors,
    // primaryColors,
    // accentColors,
    // successColors,
    // tertiaryColors,
    // warningColors,
    defaultColors
  } = useMemo(() => getStyle(theme), [theme]);
  return {
    outerContainerStyle: {
      ...outerContainer.base
    },
    circleStyle: {
      ...circle.base,
      ...(size === 'xl' ? circle.xl : {}),
      ...(size === 'l' ? circle.l : {}),
      ...(size === 'm' ? circle.m : {}),
      ...(size === 's' ? circle.s : {}),
      ...(prefersReducedMotion ? circle.slow : {})
    },
    tailChaserStyle: {
      ...tailChaser.base,
      ...(size === 'xl' ? tailChaser.xl : {}),
      ...(size === 'l' ? tailChaser.l : {}),
      ...(size === 'm' ? tailChaser.m : {}),
      ...(size === 's' ? tailChaser.s : {})
    },
    gradientColor1: defaultColors.gradientColor1,
    gradientColor2: defaultColors.gradientColor2,
    gradientColor3: defaultColors.gradientColor3,
    rail: defaultColors.rail
    // when variants are ready, uncomment and add back to props
    // ...(variant === 'danger'
    //   ? {
    //       gradientColor1: dangerColors.gradientColor1,
    //       gradientColor2: dangerColors.gradientColor2,
    //       gradientColor3: dangerColors.gradientColor3,
    //       rail: dangerColors.rail
    //     }
    //   : {}),
    // ...(variant === 'primary'
    //   ? {
    //       gradientColor1: primaryColors.gradientColor1,
    //       gradientColor2: primaryColors.gradientColor2,
    //       gradientColor3: primaryColors.gradientColor3,
    //       rail: primaryColors.rail
    //     }
    //   : {}),
    // ...(variant === 'accent'
    //   ? {
    //       gradientColor1: accentColors.gradientColor1,
    //       gradientColor2: accentColors.gradientColor2,
    //       gradientColor3: accentColors.gradientColor3,
    //       rail: accentColors.rail
    //     }
    //   : {}),
    // ...(variant === 'success'
    //   ? {
    //       gradientColor1: successColors.gradientColor1,
    //       gradientColor2: successColors.gradientColor2,
    //       gradientColor3: successColors.gradientColor3,
    //       rail: successColors.rail
    //     }
    //   : {}),
    // ...(variant === 'tertiary'
    //   ? {
    //       gradientColor1: tertiaryColors.gradientColor1,
    //       gradientColor2: tertiaryColors.gradientColor2,
    //       gradientColor3: tertiaryColors.gradientColor3,
    //       rail: tertiaryColors.rail
    //     }
    //   : {}),
    // ...(variant === 'warning'
    //   ? {
    //       gradientColor1: warningColors.gradientColor1,
    //       gradientColor2: warningColors.gradientColor2,
    //       gradientColor3: warningColors.gradientColor3,
    //       rail: warningColors.rail
    //     }
    //   : {})
  };
}
